import React, { useState, useEffect } from 'react';
import { apiService } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { 
  Plus, 
  Calendar, 
  Edit, 
  Trash2, 
  Play,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
  AlertTriangle,
  Timer,
  RotateCcw,
  Layers,
  FileCode,
  ShieldCheck,
  History,
  ChevronRight,
  ChevronDown
} from 'lucide-react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';

const FETCH_DATA_TYPES = [
  { value: 'ledgers', label: 'Ledgers' },
  { value: 'groups', label: 'Groups' },
  { value: 'stock_items', label: 'Stock Items' },
  { value: 'vouchers', label: 'Vouchers' },
  { value: 'cost_centres', label: 'Cost Centres' },
  { value: 'units', label: 'Units' },
  { value: 'godowns', label: 'Godowns' },
];

const SchedulerPage = () => {
  const [jobs, setJobs] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [tallyConnections, setTallyConnections] = useState([]);
  const [dbConnections, setDbConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingJob, setDeletingJob] = useState(null);
  const [runningJobId, setRunningJobId] = useState(null);
  const [togglingJobId, setTogglingJobId] = useState(null);
  const [previewingJobId, setPreviewingJobId] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [reconcilingJobId, setReconcilingJobId] = useState(null);
  const [reconcileData, setReconcileData] = useState(null);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRecords, setHistoryRecords] = useState([]);
  const [historyJob, setHistoryJob] = useState(null);
  const [historyLoadingJobId, setHistoryLoadingJobId] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());

  const [jobForm, setJobForm] = useState({
    name: '',
    job_type: 'post',
    dataset_id: '',
    xml_template_id: '',
    mapping_id: '',
    tally_connection_id: '',
    db_connection_id: '',
    fetch_config: { data_type: 'ledgers', company_name: '', date_from: '', date_to: '', table_name: '' },
    schedule_type: 'manual',
    schedule_value: '',
    max_retries: 0,
    batch_size: 100,
    alert_enabled: true,
    alert_recipients: []
  });

  const [alertEmail, setAlertEmail] = useState('');

  const scheduleTypes = [
    { value: 'manual', label: 'Manual Only' },
    { value: 'interval', label: 'Every X Minutes' },
    { value: 'hourly', label: 'Hourly' },
    { value: 'daily', label: 'Daily' },
    { value: 'cron', label: 'Cron Expression' }
  ];

  useEffect(() => {
    loadData();
    const interval = setInterval(loadJobs, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [jobsRes, datasetsRes, templatesRes, mappingsRes, tallyRes, dbRes] = await Promise.all([
        apiService.getJobs(),
        apiService.getDatasets(),
        apiService.getXMLTemplates(),
        apiService.getMappings(),
        apiService.getTallyConnections(),
        apiService.getDBConnections()
      ]);
      setJobs(jobsRes.data);
      setDatasets(datasetsRes.data);
      setTemplates(templatesRes.data);
      setMappings(mappingsRes.data);
      setTallyConnections(tallyRes.data);
      setDbConnections(dbRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadJobs = async () => {
    try {
      const response = await apiService.getJobs();
      setJobs(response.data);
    } catch (error) {
      console.error('Failed to refresh jobs:', error);
    }
  };

  const getDefaultForm = () => ({
    name: '',
    job_type: 'post',
    dataset_id: '',
    xml_template_id: '',
    mapping_id: '',
    tally_connection_id: '',
    db_connection_id: '',
    fetch_config: { data_type: 'ledgers', company_name: '', date_from: '', date_to: '', table_name: '' },
    schedule_type: 'manual',
    schedule_value: '',
    max_retries: 0,
    batch_size: 100,
    alert_enabled: true,
    alert_recipients: []
  });

  const openDialog = (job = null) => {
    if (job) {
      setEditingJob(job);
      setJobForm({
        name: job.name,
        job_type: job.job_type || 'post',
        dataset_id: job.dataset_id || '',
        xml_template_id: job.xml_template_id || '',
        mapping_id: job.mapping_id || '',
        tally_connection_id: job.tally_connection_id,
        db_connection_id: job.db_connection_id || '',
        fetch_config: job.fetch_config || { data_type: 'ledgers', company_name: '', date_from: '', date_to: '', table_name: '' },
        schedule_type: job.schedule_type,
        schedule_value: job.schedule_value || '',
        max_retries: job.max_retries || 0,
        batch_size: job.batch_size || 100,
        alert_enabled: job.alert_enabled,
        alert_recipients: job.alert_recipients || []
      });
    } else {
      setEditingJob(null);
      setJobForm(getDefaultForm());
    }
    setAlertEmail('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingJob(null);
  };

  const addAlertRecipient = () => {
    if (!alertEmail.trim()) return;
    if (!alertEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    if (jobForm.alert_recipients.includes(alertEmail)) {
      toast.error('Email already added');
      return;
    }
    setJobForm(prev => ({
      ...prev,
      alert_recipients: [...prev.alert_recipients, alertEmail.trim()]
    }));
    setAlertEmail('');
  };

  const removeAlertRecipient = (email) => {
    setJobForm(prev => ({
      ...prev,
      alert_recipients: prev.alert_recipients.filter(e => e !== email)
    }));
  };

  const handleSave = async () => {
    if (!jobForm.name.trim()) {
      toast.error('Please enter a job name');
      return;
    }
    if (!jobForm.tally_connection_id) {
      toast.error('Please select a Tally connection');
      return;
    }

    if (jobForm.job_type === 'post') {
      if (!jobForm.dataset_id || !jobForm.xml_template_id || !jobForm.mapping_id) {
        toast.error('Post jobs require Dataset, XML Template, and Mapping');
        return;
      }
    } else {
      if (!jobForm.db_connection_id) {
        toast.error('Fetch jobs require a Database connection');
        return;
      }
    }

    try {
      const payload = { ...jobForm };
      if (payload.job_type === 'post') {
        delete payload.fetch_config;
        delete payload.db_connection_id;
      } else {
        delete payload.dataset_id;
        delete payload.xml_template_id;
        delete payload.mapping_id;
      }

      if (editingJob) {
        await apiService.updateJob(editingJob.id, payload);
        toast.success('Job updated');
      } else {
        await apiService.createJob(payload);
        toast.success('Job created');
      }
      loadJobs();
      closeDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save job');
    }
  };

  const handleRunJob = async (jobId, mode = 'create') => {
    setRunningJobId(jobId);
    const tid = toast.loading(mode === 'alter' ? 'Sending alter/repost to Tally…' : 'Posting to Tally…');
    try {
      const res = await apiService.runJob(jobId, mode);
      const logId = res.data?.log_id;
      loadJobs();
      if (!logId) {
        toast.success('Job started', { id: tid });
        return;
      }

      // The post runs in the background — poll the run log and surface the
      // real Tally result (and any error message) in the toast.
      const deadline = Date.now() + 120000;
      let done = false;
      while (Date.now() < deadline && !done) {
        await new Promise((r) => setTimeout(r, 1500));
        let data;
        try {
          ({ data } = await apiService.getAuditLog(logId));
        } catch (e) {
          continue;
        }
        if (data.status && data.status !== 'running') {
          done = true;
          const s = data.records_success ?? 0;
          const f = data.records_failed ?? 0;
          const p = data.records_processed ?? 0;
          const skipped = data.records_skipped ?? 0;
          if (data.status !== 'success' && data.status !== 'partial') {
            const msg = data.error_message || 'Tally rejected the request';
            toast.error('Posting failed: ' + msg, { id: tid, duration: 12000 });
          } else if (f > 0) {
            const msg = data.error_message || 'Tally rejected one or more records';
            toast.error(`Partial (${s} ok, ${f} failed): ${msg}`, { id: tid, duration: 12000 });
          } else if (s > 0) {
            toast.success(
              `Posted ${s} record(s) to Tally${skipped ? ` (${skipped} already posted, skipped)` : ''}`,
              { id: tid }
            );
          } else if (skipped > 0) {
            // Nothing was sent — all rows were already posted (dedup).
            toast.message(
              `Nothing sent — all ${skipped} record(s) already posted. Change the data, or use Alter / Repost to update them in Tally.`,
              { id: tid, duration: 10000 }
            );
          } else {
            toast.message('No records to post (dataset returned no rows).', { id: tid });
          }
          loadJobs();
        }
      }
      if (!done) {
        toast.message('Still running — check Audit Logs for the result', { id: tid });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run job', { id: tid });
    } finally {
      setRunningJobId(null);
    }
  };

  const handlePreviewXml = async (jobId) => {
    setPreviewingJobId(jobId);
    try {
      const res = await apiService.previewJobXml(jobId);
      const data = res.data;
      // Log the generated XML(s) to the browser console.
      console.groupCollapsed(
        `%c[D2Tally] Generated XML — ${data.job_name} (${data.returned}/${data.total_vouchers} vouchers)`,
        'color:#0d9488;font-weight:bold'
      );
      data.vouchers.forEach((v) => {
        console.groupCollapsed(`Voucher #${v.voucher_no}  key=${v.voucher_key}  guid=${v.guid || '—'}`);
        console.log(v.xml);
        console.groupEnd();
      });
      console.groupEnd();
      setPreviewData(data);
      setPreviewOpen(true);
      toast.success(`Generated ${data.returned} XML${data.returned !== 1 ? 's' : ''} — logged to console`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate XML preview');
    } finally {
      setPreviewingJobId(null);
    }
  };

  const handleReconcile = async (jobId) => {
    setReconcilingJobId(jobId);
    try {
      const res = await apiService.reconcileJob(jobId);
      const d = res.data;
      setReconcileData(d);
      setReconcileOpen(true);
      if (d.deleted_count === 0) {
        toast.success(`All ${d.posted_count} posted ${d.type}(s) still exist in Tally`);
      } else {
        toast.message(`${d.deleted_count} posted ${d.type}(s) were deleted from Tally`, { duration: 8000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reconcile failed');
    } finally {
      setReconcilingJobId(null);
    }
  };

  const handleHistory = async (job) => {
    setHistoryLoadingJobId(job.id);
    try {
      const res = await apiService.getPostedRecords(job.id);
      setHistoryRecords(Array.isArray(res.data) ? res.data : []);
      setHistoryJob(job);
      setExpandedKeys(new Set());
      setHistoryOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load post history');
    } finally {
      setHistoryLoadingJobId(null);
    }
  };

  const toggleExpanded = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleRestoreDeleted = async (jobId) => {
    setReconcilingJobId(jobId);
    const tid = toast.loading('Re-posting deleted objects to Tally…');
    try {
      const res = await apiService.restoreDeleted(jobId);
      const logId = res.data?.log_id;
      if (!logId) {
        toast.message(res.data?.message || 'Nothing to restore', { id: tid });
        return;
      }
      setReconcileOpen(false);
      // Poll the run log for the result (same pattern as Run Now).
      const deadline = Date.now() + 120000;
      let done = false;
      while (Date.now() < deadline && !done) {
        await new Promise((r) => setTimeout(r, 1500));
        let data;
        try { ({ data } = await apiService.getAuditLog(logId)); } catch (e) { continue; }
        if (data.status && data.status !== 'running') {
          done = true;
          const s = data.records_success ?? 0;
          const f = data.records_failed ?? 0;
          if (f > 0) {
            toast.error(`Restore failed: ${data.error_message || 'Tally rejected the request'}`, { id: tid, duration: 12000 });
          } else {
            toast.success(`Re-posted ${s} object(s) to Tally`, { id: tid });
          }
          loadJobs();
        }
      }
      if (!done) toast.message('Still running — check Audit Logs', { id: tid });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Restore failed', { id: tid });
    } finally {
      setReconcilingJobId(null);
    }
  };

  const handleToggleJob = async (jobId) => {
    setTogglingJobId(jobId);
    try {
      const result = await apiService.toggleJob(jobId);
      toast.success(result.data.message);
      loadJobs();
    } catch (error) {
      toast.error('Failed to toggle job');
    } finally {
      setTogglingJobId(null);
    }
  };

  const openDeleteDialog = (job) => {
    setDeletingJob(job);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingJob) return;
    try {
      await apiService.deleteJob(deletingJob.id);
      toast.success('Job deleted');
      loadJobs();
    } catch (error) {
      toast.error('Failed to delete job');
    } finally {
      setDeleteDialogOpen(false);
      setDeletingJob(null);
    }
  };

  const getStatusBadge = (status) => {
    if (!status) return <Badge className="badge-neutral">-</Badge>;
    if (status.startsWith('retry_')) {
      return (
        <Badge className="bg-amber-100 text-amber-700 border-amber-200">
          <RotateCcw className="h-3 w-3 mr-1" />
          Retry {status.replace('retry_', '')}
        </Badge>
      );
    }
    switch (status) {
      case 'success':
        return <Badge className="badge-success"><CheckCircle2 className="h-3 w-3 mr-1" />Success</Badge>;
      case 'failed':
        return <Badge className="badge-error"><XCircle className="h-3 w-3 mr-1" />Failed</Badge>;
      case 'running':
        return <Badge className="badge-warning"><RefreshCw className="h-3 w-3 mr-1 animate-spin" />Running</Badge>;
      case 'partial':
        return <Badge className="bg-orange-100 text-orange-700 border-orange-200"><AlertTriangle className="h-3 w-3 mr-1" />Partial</Badge>;
      default:
        return <Badge className="badge-neutral">-</Badge>;
    }
  };

  const canCreateJob = tallyConnections.length > 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="scheduler-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Scheduler</h1>
          <p className="page-subtitle">Manage and monitor automated sync jobs</p>
        </div>
        <Button 
          className="bg-teal-700 hover:bg-teal-600"
          onClick={() => openDialog()}
          disabled={!canCreateJob}
          data-testid="create-job-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Job
        </Button>
      </div>

      {!canCreateJob && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <div>
                <p className="font-medium text-amber-800">Setup Required</p>
                <p className="text-sm text-amber-600">Create at least one Tally connection to start creating jobs.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="table-header">
                <TableHead className="table-cell">Job Name</TableHead>
                <TableHead className="table-cell">Type</TableHead>
                <TableHead className="table-cell">Schedule</TableHead>
                <TableHead className="table-cell">Last Run</TableHead>
                <TableHead className="table-cell">Status</TableHead>
                <TableHead className="table-cell">Next Run</TableHead>
                <TableHead className="table-cell">Active</TableHead>
                <TableHead className="table-cell text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                    <Calendar className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No jobs created yet</p>
                    <p className="text-sm">Click "Create Job" to schedule automated syncs</p>
                  </TableCell>
                </TableRow>
              ) : (
                jobs.map((job) => (
                  <TableRow key={job.id} className="table-row" data-testid={`job-row-${job.id}`}>
                    <TableCell className="table-cell">
                      <div className="font-medium">{job.name}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {job.alert_enabled && <span className="text-xs text-slate-400">Alerts on</span>}
                        {job.max_retries > 0 && (
                          <span className="text-xs text-slate-400 flex items-center gap-0.5">
                            <RotateCcw className="h-2.5 w-2.5" />{job.max_retries} retries
                          </span>
                        )}
                        {job.batch_size && job.batch_size !== 100 && (
                          <span className="text-xs text-slate-400 flex items-center gap-0.5">
                            <Layers className="h-2.5 w-2.5" />Batch: {job.batch_size}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="table-cell">
                      <Badge className={job.job_type === 'fetch' ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-teal-100 text-teal-700 border-teal-200'}>
                        {job.job_type === 'fetch' ? 'Fetch' : 'Post'}
                      </Badge>
                    </TableCell>
                    <TableCell className="table-cell">
                      <div className="flex items-center gap-2">
                        <Clock className="h-3.5 w-3.5 text-slate-400" />
                        <span className="text-sm">
                          {job.schedule_type === 'manual' ? 'Manual' :
                           job.schedule_type === 'interval' ? `Every ${job.schedule_value} min` :
                           job.schedule_type === 'hourly' ? 'Hourly' :
                           job.schedule_type === 'daily' ? 'Daily' :
                           job.schedule_value}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="table-cell">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-sm text-slate-600 cursor-default">
                              {job.last_run ? formatDistanceToNow(parseISO(job.last_run), { addSuffix: true }) : '-'}
                            </span>
                          </TooltipTrigger>
                          {job.last_run && (
                            <TooltipContent>
                              <p>{format(parseISO(job.last_run), 'MMM d, yyyy HH:mm:ss')}</p>
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell className="table-cell">
                      {getStatusBadge(job.last_status)}
                    </TableCell>
                    <TableCell className="table-cell" data-testid={`next-run-${job.id}`}>
                      {job.next_run ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-1.5">
                                <Timer className="h-3.5 w-3.5 text-teal-500" />
                                <span className="text-sm font-medium text-teal-700">
                                  {formatDistanceToNow(parseISO(job.next_run), { addSuffix: true })}
                                </span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{format(parseISO(job.next_run), 'MMM d, yyyy HH:mm:ss')}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <span className="text-xs text-slate-400">
                          {job.schedule_type === 'manual' ? 'Manual only' : 
                           !job.is_active ? 'Inactive' : '-'}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="table-cell">
                      <Switch
                        checked={job.is_active}
                        onCheckedChange={() => handleToggleJob(job.id)}
                        disabled={togglingJobId === job.id || job.schedule_type === 'manual'}
                        data-testid={`toggle-job-${job.id}`}
                      />
                    </TableCell>
                    <TableCell className="table-cell text-right">
                      <div className="flex items-center justify-end gap-1">
                        {job.job_type !== 'fetch' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePreviewXml(job.id)}
                            disabled={previewingJobId === job.id}
                            className="text-slate-700 border-slate-200 hover:bg-slate-50 gap-1.5 h-8"
                            title="Generate the XML without sending to Tally (also logged to browser console)"
                            data-testid={`preview-job-${job.id}`}
                          >
                            {previewingJobId === job.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <FileCode className="h-3.5 w-3.5" />
                            )}
                            <span className="text-xs">Preview XML</span>
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRunJob(job.id)}
                          disabled={runningJobId === job.id}
                          className="text-teal-700 border-teal-200 hover:bg-teal-50 gap-1.5 h-8"
                          data-testid={`run-job-${job.id}`}
                        >
                          {runningJobId === job.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Play className="h-3.5 w-3.5" />
                          )}
                          <span className="text-xs">Run Now</span>
                        </Button>
                        {job.job_type !== 'fetch' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRunJob(job.id, 'alter')}
                            disabled={runningJobId === job.id}
                            className="text-violet-700 border-violet-200 hover:bg-violet-50 gap-1.5 h-8"
                            title="Re-send previously posted vouchers with ACTION=Alter using their stored GUID"
                            data-testid={`alter-job-${job.id}`}
                          >
                            <RefreshCw className="h-3.5 w-3.5" />
                            <span className="text-xs">Alter / Repost</span>
                          </Button>
                        )}
                        {job.job_type !== 'fetch' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleHistory(job)}
                            disabled={historyLoadingJobId === job.id}
                            className="text-amber-700 border-amber-200 hover:bg-amber-50 gap-1.5 h-8"
                            title="View the post history of each record — success or the failure reason"
                            data-testid={`history-job-${job.id}`}
                          >
                            {historyLoadingJobId === job.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <History className="h-3.5 w-3.5" />
                            )}
                            <span className="text-xs">History</span>
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => openDialog(job)} data-testid={`edit-job-${job.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openDeleteDialog(job)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-job-${job.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{editingJob ? 'Edit Job' : 'Create Job'}</DialogTitle>
            <DialogDescription>Configure the job type, connections, and schedule</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[65vh] overflow-y-auto pr-1">
            {/* Job Name & Type */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Job Name *</Label>
                <Input
                  value={jobForm.name}
                  onChange={(e) => setJobForm({ ...jobForm, name: e.target.value })}
                  placeholder="e.g., Daily Sales Sync"
                  data-testid="job-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Job Type *</Label>
                <Select
                  value={jobForm.job_type}
                  onValueChange={(value) => setJobForm({ ...jobForm, job_type: value })}
                  disabled={!!editingJob}
                >
                  <SelectTrigger data-testid="job-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="post">Post (DB to Tally)</SelectItem>
                    <SelectItem value="fetch">Fetch (Tally to DB)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Post-specific fields */}
            {jobForm.job_type === 'post' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Dataset *</Label>
                    <Select
                      value={jobForm.dataset_id}
                      onValueChange={(value) => setJobForm({ ...jobForm, dataset_id: value })}
                    >
                      <SelectTrigger data-testid="job-dataset-select">
                        <SelectValue placeholder="Select dataset" />
                      </SelectTrigger>
                      <SelectContent>
                        {datasets.map((ds) => (
                          <SelectItem key={ds.id} value={ds.id}>{ds.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>XML Template *</Label>
                    <Select
                      value={jobForm.xml_template_id}
                      onValueChange={(value) => setJobForm({ ...jobForm, xml_template_id: value })}
                    >
                      <SelectTrigger data-testid="job-template-select">
                        <SelectValue placeholder="Select template" />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map((t) => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Mapping *</Label>
                    <Select
                      value={jobForm.mapping_id}
                      onValueChange={(value) => setJobForm({ ...jobForm, mapping_id: value })}
                    >
                      <SelectTrigger data-testid="job-mapping-select">
                        <SelectValue placeholder="Select mapping" />
                      </SelectTrigger>
                      <SelectContent>
                        {mappings.map((m) => (
                          <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tally Connection *</Label>
                    <Select
                      value={jobForm.tally_connection_id}
                      onValueChange={(value) => setJobForm({ ...jobForm, tally_connection_id: value })}
                    >
                      <SelectTrigger data-testid="job-tally-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {tallyConnections.map((tc) => (
                          <SelectItem key={tc.id} value={tc.id}>{tc.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}

            {/* Fetch-specific fields */}
            {jobForm.job_type === 'fetch' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Tally Connection *</Label>
                    <Select
                      value={jobForm.tally_connection_id}
                      onValueChange={(value) => setJobForm({ ...jobForm, tally_connection_id: value })}
                    >
                      <SelectTrigger data-testid="job-tally-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {tallyConnections.map((tc) => (
                          <SelectItem key={tc.id} value={tc.id}>{tc.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Database Connection *</Label>
                    <Select
                      value={jobForm.db_connection_id}
                      onValueChange={(value) => setJobForm({ ...jobForm, db_connection_id: value })}
                    >
                      <SelectTrigger data-testid="job-db-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {dbConnections.map((dc) => (
                          <SelectItem key={dc.id} value={dc.id}>{dc.name} ({dc.db_type})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Data Type</Label>
                    <Select
                      value={jobForm.fetch_config.data_type}
                      onValueChange={(value) => setJobForm({ ...jobForm, fetch_config: { ...jobForm.fetch_config, data_type: value } })}
                    >
                      <SelectTrigger data-testid="job-data-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FETCH_DATA_TYPES.map((dt) => (
                          <SelectItem key={dt.value} value={dt.value}>{dt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Target Table Name</Label>
                    <Input
                      value={jobForm.fetch_config.table_name}
                      onChange={(e) => setJobForm({ ...jobForm, fetch_config: { ...jobForm.fetch_config, table_name: e.target.value } })}
                      placeholder="e.g., tally_ledgers"
                      data-testid="job-table-name-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Company Name</Label>
                    <Input
                      value={jobForm.fetch_config.company_name}
                      onChange={(e) => setJobForm({ ...jobForm, fetch_config: { ...jobForm.fetch_config, company_name: e.target.value } })}
                      placeholder="From Tally connection"
                      data-testid="job-company-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date Range</Label>
                    <div className="flex gap-2">
                      <Input
                        value={jobForm.fetch_config.date_from}
                        onChange={(e) => setJobForm({ ...jobForm, fetch_config: { ...jobForm.fetch_config, date_from: e.target.value } })}
                        placeholder="01-Apr-2024"
                        className="text-xs"
                      />
                      <Input
                        value={jobForm.fetch_config.date_to}
                        onChange={(e) => setJobForm({ ...jobForm, fetch_config: { ...jobForm.fetch_config, date_to: e.target.value } })}
                        placeholder="31-Mar-2025"
                        className="text-xs"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Schedule */}
            <div className="border-t border-slate-200 pt-4">
              <Label className="text-sm font-semibold text-slate-700 mb-3 block">Schedule</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs">Schedule Type</Label>
                  <Select
                    value={jobForm.schedule_type}
                    onValueChange={(value) => setJobForm({ ...jobForm, schedule_type: value })}
                  >
                    <SelectTrigger data-testid="job-schedule-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {scheduleTypes.map((st) => (
                        <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {(jobForm.schedule_type === 'interval' || jobForm.schedule_type === 'cron') && (
                  <div className="space-y-2">
                    <Label className="text-xs">{jobForm.schedule_type === 'cron' ? 'Cron Expression' : 'Minutes'}</Label>
                    <Input
                      value={jobForm.schedule_value}
                      onChange={(e) => setJobForm({ ...jobForm, schedule_value: e.target.value })}
                      placeholder={jobForm.schedule_type === 'cron' ? '0 */6 * * *' : '30'}
                      data-testid="job-schedule-value-input"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Advanced: Retry & Batch */}
            <div className="border-t border-slate-200 pt-4">
              <Label className="text-sm font-semibold text-slate-700 mb-3 block">Advanced</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs">Max Retries on Failure</Label>
                  <Select
                    value={String(jobForm.max_retries)}
                    onValueChange={(value) => setJobForm({ ...jobForm, max_retries: parseInt(value) })}
                  >
                    <SelectTrigger data-testid="job-max-retries-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">No Retries</SelectItem>
                      <SelectItem value="1">1 Retry</SelectItem>
                      <SelectItem value="2">2 Retries</SelectItem>
                      <SelectItem value="3">3 Retries</SelectItem>
                      <SelectItem value="5">5 Retries</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Batch Size</Label>
                  <Select
                    value={String(jobForm.batch_size)}
                    onValueChange={(value) => setJobForm({ ...jobForm, batch_size: parseInt(value) })}
                  >
                    <SelectTrigger data-testid="job-batch-size-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10 records</SelectItem>
                      <SelectItem value="50">50 records</SelectItem>
                      <SelectItem value="100">100 records</SelectItem>
                      <SelectItem value="500">500 records</SelectItem>
                      <SelectItem value="1000">1000 records</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {jobForm.max_retries > 0 && (
                <p className="text-xs text-slate-500 mt-2">
                  Retries with exponential backoff: 1min, 5min, 15min, 30min, 1hr
                </p>
              )}
            </div>

            {/* Alerts */}
            <div className="border-t border-slate-200 pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label>Failure Alerts</Label>
                <Switch
                  checked={jobForm.alert_enabled}
                  onCheckedChange={(checked) => setJobForm({ ...jobForm, alert_enabled: checked })}
                  data-testid="job-alert-toggle"
                />
              </div>
              
              {jobForm.alert_enabled && (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      value={alertEmail}
                      onChange={(e) => setAlertEmail(e.target.value)}
                      placeholder="email@example.com"
                      className="flex-1"
                      onKeyDown={(e) => e.key === 'Enter' && addAlertRecipient()}
                    />
                    <Button variant="outline" onClick={addAlertRecipient}>Add</Button>
                  </div>
                  {jobForm.alert_recipients.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {jobForm.alert_recipients.map((email) => (
                        <Badge key={email} className="badge-neutral">
                          {email}
                          <button onClick={() => removeAlertRecipient(email)} className="ml-2 text-slate-500 hover:text-slate-700">x</button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button 
              className="bg-teal-700 hover:bg-teal-600"
              onClick={handleSave}
              data-testid="save-job-btn"
            >
              {editingJob ? 'Update' : 'Create'} Job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Job</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deletingJob?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-job-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* XML Preview (dry-run) Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCode className="h-4 w-4 text-teal-600" />
              Generated XML — {previewData?.job_name}
            </DialogTitle>
            <DialogDescription>
              Dry run — nothing was sent to Tally. Showing {previewData?.returned} of{' '}
              {previewData?.total_vouchers} voucher(s). Full output is also in the browser console.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 pr-1">
            {previewData?.vouchers?.length ? (
              previewData.vouchers.map((v) => (
                <div key={v.voucher_no} className="space-y-1">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Badge className="badge-neutral">#{v.voucher_no}</Badge>
                    <span className="font-mono truncate">key: {v.voucher_key}</span>
                    {v.guid && (
                      <span className="font-mono text-violet-600 truncate">
                        guid: {v.guid}{v.is_sample_guid ? ' (sample)' : ''}
                      </span>
                    )}
                  </div>
                  <pre className="p-3 bg-slate-900 text-slate-100 rounded-lg text-xs overflow-auto max-h-64 font-mono whitespace-pre-wrap break-words">
                    {v.xml}
                  </pre>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-500 py-8 text-center">No vouchers generated for this job.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reconcile (deleted-in-Tally) Dialog */}
      <Dialog open={reconcileOpen} onOpenChange={setReconcileOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-amber-600" />
              Reconcile — {reconcileData?.job_name}
            </DialogTitle>
            <DialogDescription>
              Compared {reconcileData?.posted_count} posted {reconcileData?.type}(s) against Tally
              (company {reconcileData?.company}). {reconcileData?.existing_in_tally} exist in Tally now.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {reconcileData?.deleted_count === 0 ? (
              <div className="py-8 text-center text-emerald-700">
                <ShieldCheck className="h-10 w-10 mx-auto mb-2 opacity-60" />
                <p className="font-medium">Nothing deleted — all posted objects still exist in Tally.</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-amber-700 mb-2">
                  {reconcileData?.deleted_count} object(s) were posted before but are now missing from Tally:
                </p>
                <div className="border border-slate-200 rounded-lg divide-y">
                  {reconcileData?.deleted?.map((d, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 text-sm">
                      <span className="h-2 w-2 rounded-full bg-amber-500 shrink-0" />
                      <span className="font-medium truncate">{d.name}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-3">
                  Click <strong>Re-post deleted</strong> to re-create them in Tally (they'll get a new
                  Tally GUID). Note: if Tally keeps dropping them, the company's save/persistence
                  must be fixed on the Tally server.
                </p>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReconcileOpen(false)}>Close</Button>
            {reconcileData?.deleted_count > 0 && (
              <Button
                className="bg-teal-700 hover:bg-teal-600"
                onClick={() => handleRestoreDeleted(reconcileData.job_id)}
                disabled={reconcilingJobId === reconcileData?.job_id}
                data-testid="restore-deleted-btn"
              >
                {reconcilingJobId === reconcileData?.job_id ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Re-post deleted ({reconcileData.deleted_count})
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post History Dialog — per-record post outcome (success / failure reason) */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-4 w-4 text-amber-600" />
              Post history — {historyJob?.name}
            </DialogTitle>
            <DialogDescription>
              Each record's latest result and full attempt history. A green badge means it
              posted to Tally; a red badge shows the failure reason. Click a row to expand.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {historyRecords.length === 0 ? (
              <div className="py-10 text-center text-slate-500">
                <History className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No records have been posted for this job yet.</p>
              </div>
            ) : (
              <div className="border border-slate-200 rounded-lg divide-y">
                {historyRecords.map((r) => {
                  const key = r.voucher_key;
                  const status = r.last_status || (r.status === 'success' ? 'success' : 'unknown');
                  const ok = status === 'success';
                  const failed = status === 'failed';
                  const when = r.last_attempt_at || r.posted_at;
                  const name = r.record_name || r.voucher_key;
                  const history = Array.isArray(r.history) ? [...r.history].reverse() : [];
                  const expanded = expandedKeys.has(key);
                  return (
                    <div key={key}>
                      <button
                        type="button"
                        onClick={() => toggleExpanded(key)}
                        className="w-full flex items-start gap-2 px-3 py-2.5 text-left hover:bg-slate-50"
                        data-testid={`history-row-${key}`}
                      >
                        {expanded
                          ? <ChevronDown className="h-4 w-4 mt-0.5 text-slate-400 shrink-0" />
                          : <ChevronRight className="h-4 w-4 mt-0.5 text-slate-400 shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm truncate">{name}</span>
                            {ok && (
                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 gap-1">
                                <CheckCircle2 className="h-3 w-3" /> Success
                              </Badge>
                            )}
                            {failed && (
                              <Badge className="bg-red-100 text-red-700 hover:bg-red-100 gap-1">
                                <XCircle className="h-3 w-3" /> Failed
                              </Badge>
                            )}
                            {!ok && !failed && <Badge variant="secondary">{status}</Badge>}
                            {r.last_action && (
                              <span className="text-[11px] uppercase tracking-wide text-slate-400">{r.last_action}</span>
                            )}
                          </div>
                          {failed && r.last_error && (
                            <p className="text-xs text-red-600 mt-0.5 break-words">{r.last_error}</p>
                          )}
                          {ok && <p className="text-xs text-emerald-700 mt-0.5">Posted to Tally successfully</p>}
                        </div>
                        {when && (
                          <span className="text-[11px] text-slate-400 shrink-0 whitespace-nowrap">
                            {formatDistanceToNow(parseISO(when), { addSuffix: true })}
                          </span>
                        )}
                      </button>
                      {expanded && (
                        <div className="bg-slate-50 px-3 py-2 border-t border-slate-100">
                          {history.length === 0 ? (
                            <p className="text-xs text-slate-400">No attempt history recorded.</p>
                          ) : (
                            <ul className="space-y-1.5">
                              {history.map((h, i) => (
                                <li key={i} className="flex items-start gap-2 text-xs">
                                  {h.status === 'success'
                                    ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />
                                    : <XCircle className="h-3.5 w-3.5 text-red-500 mt-0.5 shrink-0" />}
                                  <span className="text-slate-400 shrink-0 whitespace-nowrap">
                                    {h.at ? format(parseISO(h.at), 'dd MMM HH:mm:ss') : ''}
                                  </span>
                                  <span className="uppercase text-slate-400 shrink-0">{h.action}</span>
                                  <span className={h.status === 'success' ? 'text-emerald-700' : 'text-red-600 break-words'}>
                                    {h.status === 'success' ? 'Success' : (h.error || 'Failed')}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SchedulerPage;
